import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTER_LINKS } from '../../consts/app-keys.const';
import useAuth from '../../../auth/feature/auth';
import { authService } from '../../../auth/service/auth.service';
import { useTheme } from '../../hooks/use-theme';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' }
];

/**
 * The public nav, in the order it reads. Three places render this same list —
 * the desktop bar and both mobile menus, signed in and signed out — so it
 * lives here rather than being typed out three times. Kept as three copies it
 * had already drifted: "Developer" was the one label nobody had wired to a
 * translation key, so it stayed English in all eleven languages.
 *
 * The icon is drawn only by the mobile menus. The desktop bar is text alone,
 * and ignores it.
 */
const NAV_ITEMS: { to: string; labelKey: string; icon: React.ReactNode }[] = [
  {
    to: ROUTER_LINKS.DOCS,
    labelKey: 'header.docs',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
    )
  },
  {
    to: ROUTER_LINKS.DEVELOPER,
    labelKey: 'header.developer',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
    )
  },
  {
    to: ROUTER_LINKS.PRICING,
    labelKey: 'header.pricing',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
    )
  },
  {
    to: ROUTER_LINKS.CONTACT,
    labelKey: 'header.contact',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
    )
  }
];

/** Repeated by every row in both mobile menus, nav items and account links alike. */
const MOBILE_LINK =
  'flex items-center gap-[10px] font-body text-[16px] text-base-black dark:text-white py-[8px]';

const DESKTOP_LINK =
  'h-[40px] inline-flex items-center font-body text-[14px] text-[#818185] hover:text-base-black dark:hover:text-white transition-colors';

export const Header = () => {
  const { isAuthenticated, setIsAuthenticated, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [langOpen, setLangOpen] = React.useState(false);
  const langRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const [showLogoutModal, setShowLogoutModal] = React.useState(false);

  const confirmLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setMenuOpen(false);
    setMobileMenuOpen(false);
    setShowLogoutModal(false);
    navigate(ROUTER_LINKS.ROOT);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    setMobileMenuOpen(false);
    setShowLogoutModal(true);
  };

  const closeMobile = () => setMobileMenuOpen(false);
  const userName = user?.displayName || user?.email?.split('@')[0] || '';
  const userInitials = userName.slice(0, 2).toUpperCase();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    if (!menuOpen && !langOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (langOpen && langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, langOpen]);

  return (
    <header className="py-[16px] md:pt-[14px] md:pb-0 sticky top-0 z-30 bg-white dark:bg-[#121214] -mx-[5vw] px-[5vw] md:mx-0 md:px-0">
      {/* Desktop: outlined menu bar */}
      <div className="hidden md:flex items-center justify-between h-[56px] px-[6px] pl-[24px] rounded-[32px] border border-[#E5E5ED] dark:border-[#2E2E30] bg-white dark:bg-[#1D1D1E] shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        {/* Left: Logo + nav links */}
        <div className="flex items-center gap-[28px]">
          <Link
            to={ROUTER_LINKS.ROOT}
            className="h-[40px] flex items-center gap-[8px] font-display text-[20px] md:text-[22px] font-bold text-base-black dark:text-white"
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="32" r="9" fill="#0ea5c9"/>
              <circle cx="32" cy="32" r="9" fill="currentColor"/>
              <circle cx="52" cy="32" r="9" fill="currentColor" opacity="0.3"/>
            </svg>
            Standby
          </Link>
          {NAV_ITEMS.map((item) => (
            <Link key={item.to} to={item.to} className={DESKTOP_LINK}>
              {t(item.labelKey)}
            </Link>
          ))}
        </div>

        {/* Right: user controls */}
        <div className="flex items-center gap-[8px]">
          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            className="h-[40px] w-[40px] flex items-center justify-center rounded-[32px] text-[#818185] hover:text-base-black dark:hover:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#2A2A2C] transition-all"
          >
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            )}
          </button>
          {/* Language pill */}
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen(!langOpen)}
              className={`h-[40px] px-[12px] flex items-center gap-[6px] rounded-[32px] font-body text-[13px] font-medium transition-all ${
                langOpen
                  ? 'bg-[#EFEFF1] dark:bg-[#2A2A2C] text-base-black dark:text-white'
                  : 'text-[#818185] hover:text-base-black dark:hover:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#2A2A2C]'
              }`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              {LANGUAGES.find((l) => l.code === i18n.language)?.label || 'English'}
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-[8px] min-w-[160px] bg-white dark:bg-[#1D1D1E] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[#2E2E30] shadow-[0_4px_20px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.5)] py-[6px] z-50 animate-fadeInMenu">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-[14px] py-[8px] font-body text-[13px] text-[#111827] dark:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#2A2A2C] transition-colors ${
                      lang.code === i18n.language ? 'font-semibold' : ''
                    }`}
                  >
                    <span>{lang.label}</span>
                    {lang.code === i18n.language && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isAuthenticated ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="h-[40px] pl-[4px] pr-[14px] flex items-center gap-[8px] border border-gray-500 dark:border-[#3A3A3C] bg-gray-400 dark:bg-[#2A2A2C] rounded-[32px] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#323234] transition-all"
              >
                <span className="w-[32px] h-[32px] rounded-full bg-[#0ea5c9] text-white flex items-center justify-center text-[12px] font-bold uppercase">
                  {userInitials || '?'}
                </span>
                <span className="max-w-[140px] truncate">{userName}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`flex-shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`}>
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-[8px] bg-white dark:bg-[#1D1D1E] rounded-[16px] border border-transparent dark:border-[#2E2E30] shadow-[0px_4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0px_4px_20px_rgba(0,0,0,0.5)] py-[8px] min-w-[200px] z-50">
                  <div className="px-[16px] py-[8px] border-b border-[rgba(0,0,0,0.07)] dark:border-white/10 mb-[4px]">
                    <p className="font-body text-[13px] font-semibold text-base-black dark:text-white truncate">
                      {userName}
                    </p>
                    <p className="font-body text-[11px] text-[#818185] truncate">
                      {user?.email}
                    </p>
                  </div>
                  {isAdmin && (
                    <Link
                      to={ROUTER_LINKS.ADMIN}
                      className="flex items-center gap-[10px] px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                      onClick={() => setMenuOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                      {t('header.admin')}
                    </Link>
                  )}
                  <Link
                    to={ROUTER_LINKS.DASHBOARD}
                    className="flex items-center gap-[10px] px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                    {t('header.dashboard')}
                  </Link>
                  <Link
                    to={ROUTER_LINKS.TEAMS}
                    className="flex items-center gap-[10px] px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    {t('header.teams')}
                  </Link>
                  {user?.isEnterprise && (
                    <Link
                      to={ROUTER_LINKS.COMPANY}
                      className="flex items-center gap-[10px] px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                      onClick={() => setMenuOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      {t('header.company')}
                    </Link>
                  )}
                  <Link
                    to={ROUTER_LINKS.SETTINGS}
                    className="flex items-center gap-[10px] px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                    {t('header.settings')}
                  </Link>
                  <div className="my-[4px] border-t border-[rgba(0,0,0,0.07)] dark:border-white/10" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center gap-[10px] w-full text-left px-[16px] py-[8px] font-body text-[14px] text-base-black dark:text-white hover:bg-gray-400 dark:hover:bg-[#2A2A2C]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to={ROUTER_LINKS.SIGNIN}
                className="h-[40px] px-[20px] flex items-center border border-gray-500 dark:border-[#3A3A3C] bg-gray-400 dark:bg-[#2A2A2C] rounded-[32px] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#323234] transition-all"
              >
                {t('header.login')}
              </Link>
              <Link
                to={ROUTER_LINKS.SIGNUP}
                className="h-[40px] px-[20px] flex items-center bg-base-black dark:bg-white text-white dark:text-base-black rounded-[32px] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#E5E5ED] transition-all"
              >
                {t('header.signup')}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile: simple bar */}
      <div className="flex md:hidden items-center justify-between">
        <Link
          to={ROUTER_LINKS.ROOT}
          className="flex items-center gap-[8px] font-display text-[20px] font-bold text-base-black dark:text-white"
        >
          <svg width="22" height="22" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="32" r="9" fill="#0ea5c9"/>
            <circle cx="32" cy="32" r="9" fill="currentColor"/>
            <circle cx="52" cy="32" r="9" fill="currentColor" opacity="0.3"/>
          </svg>
          Standby
        </Link>
        <div className="flex items-center gap-[4px]">
          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            className="h-[40px] w-[40px] flex items-center justify-center rounded-[32px] text-[#818185] hover:text-base-black dark:hover:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#2A2A2C] transition-all"
          >
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            )}
          </button>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-[40px] h-[40px] flex flex-col items-center justify-center gap-[5px]"
          >
            <span style={{ backgroundColor: isDark ? '#6B6B6F' : '#818185' }} className={`block w-[20px] h-[2px] transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span style={{ backgroundColor: isDark ? '#6B6B6F' : '#818185' }} className={`block w-[20px] h-[2px] transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
            <span style={{ backgroundColor: isDark ? '#6B6B6F' : '#818185' }} className={`block w-[20px] h-[2px] transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white dark:bg-[#1D1D1E] rounded-[16px] border border-transparent dark:border-[#2E2E30] shadow-[0px_4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0px_4px_20px_rgba(0,0,0,0.5)] p-[16px] z-50 flex flex-col gap-[12px] md:hidden">
          {isAuthenticated ? (
            <>
              <div className="flex items-center gap-[10px] py-[8px]">
                <span className="w-[28px] h-[28px] rounded-full bg-[#0ea5c9] text-white flex items-center justify-center text-[12px] font-semibold uppercase shrink-0">
                  {userInitials || '?'}
                </span>
                <div>
                  <p className="font-body text-[14px] font-semibold text-base-black dark:text-white truncate">
                    {userName}
                  </p>
                  <p className="font-body text-[11px] text-[#818185] truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <div className="my-[4px] border-t border-[rgba(0,0,0,0.07)] dark:border-white/10" />
              {isAdmin && (
                <Link
                  to={ROUTER_LINKS.ADMIN}
                  className={MOBILE_LINK}
                  onClick={closeMobile}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  {t('header.admin')}
                </Link>
              )}
              <Link
                to={ROUTER_LINKS.DASHBOARD}
                className={MOBILE_LINK}
                onClick={closeMobile}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                {t('header.dashboard')}
              </Link>
              <Link
                to={ROUTER_LINKS.TEAMS}
                className={MOBILE_LINK}
                onClick={closeMobile}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                {t('header.teams')}
              </Link>
              {user?.isEnterprise && (
                <Link
                  to={ROUTER_LINKS.COMPANY}
                  className={MOBILE_LINK}
                  onClick={closeMobile}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  {t('header.company')}
                </Link>
              )}
              <Link
                to={ROUTER_LINKS.SETTINGS}
                className={MOBILE_LINK}
                onClick={closeMobile}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                {t('header.settings')}
              </Link>
              <div className="my-[4px] border-t border-[rgba(0,0,0,0.07)] dark:border-white/10" />
              {NAV_ITEMS.map((item) => (
                <Link key={item.to} to={item.to} className={MOBILE_LINK} onClick={closeMobile}>
                  {item.icon}
                  {t(item.labelKey)}
                </Link>
              ))}
              <div className="my-[4px] border-t border-[rgba(0,0,0,0.07)] dark:border-white/10" />
              <button
                type="button"
                onClick={handleLogout}
                className={`${MOBILE_LINK} text-left`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                {t('header.logout')}
              </button>
            </>
          ) : (
            <>
              {NAV_ITEMS.map((item) => (
                <Link key={item.to} to={item.to} className={MOBILE_LINK} onClick={closeMobile}>
                  {item.icon}
                  {t(item.labelKey)}
                </Link>
              ))}
              <div className="flex gap-[8px] pt-[4px]">
                <Link
                  to={ROUTER_LINKS.SIGNIN}
                  className="flex-1 h-[42px] flex items-center justify-center border border-gray-500 dark:border-[#3A3A3C] bg-gray-400 dark:bg-[#2A2A2C] rounded-[32px] font-body text-[14px] font-medium text-base-black dark:text-white transition-all"
                  onClick={closeMobile}
                >
                  {t('header.login')}
                </Link>
                <Link
                  to={ROUTER_LINKS.SIGNUP}
                  className="flex-1 h-[42px] flex items-center justify-center bg-base-black dark:bg-white text-white dark:text-base-black rounded-[32px] font-body text-[14px] font-medium transition-all"
                  onClick={closeMobile}
                >
                  {t('header.signup')}
                </Link>
              </div>
            </>
          )}
        </div>
      )}
      {showLogoutModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLogoutModal(false); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('header.logout')}
              </h2>
              <p className="font-body text-[14px] text-gray-900">
                {t('header.logoutConfirmMessage')}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={confirmLogout}
                className="flex-1 h-[44px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
              >
                {t('header.logoutConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
